import ReturnIcon from "@redotech/redo-icons/flip-backward.svg";
import ZapIconFilled from "@redotech/redo-icons/zap_filled.svg";
import { FlowType, Step as ModelStep } from "@redotech/redo-model/return-flow";
import {
  buildExchangeAgentOverrides,
  returnFromModel,
} from "@redotech/redo-model/return-flow-step-from-model";
import {
  isReturnStepValid,
  ReturnState,
} from "@redotech/redo-model/return-flow-step-valid";
import { isExchangeAgentEnabledForTeam } from "@redotech/redo-model/return-flow/exchange-agent/exchange-agent-utils";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "@redotech/redo-model/return-flow/return-option";
import { Team } from "@redotech/redo-model/team";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

/**
 * Aligns a per-currency override's sign with the adjustment's flat amount so
 * a fee (negative flat) stays a fee across every currency. Overrides are
 * edited as absolute values in the UI; the sign is derived at submit time.
 */
export function signCurrencyOverride(
  overrideValue: number,
  flat: number,
): number {
  if (flat < 0) {
    return -Math.abs(overrideValue);
  }
  if (flat > 0) {
    return Math.abs(overrideValue);
  }
  return overrideValue;
}

export function getModelReturnOptionMethod(
  type: ReturnOptionMethod["type"],
  creditType: StoreCreditType,
): ReturnOptionMethod {
  switch (type) {
    case ReturnOptionMethod.STORE_CREDIT:
      return { type: ReturnOptionMethod.STORE_CREDIT, creditType };
    case ReturnOptionMethod.EXCHANGE:
      return { type: ReturnOptionMethod.EXCHANGE };
    case ReturnOptionMethod.REFUND:
      return { type: ReturnOptionMethod.REFUND };
    case ReturnOptionMethod.REPAIR:
      return { type: ReturnOptionMethod.REPAIR };
  }
}

export const RETURN = (
  Details: React.ComponentType<StepTypeDetailsProps<ReturnState>>,
): StepType<ReturnState, ModelStep.Return> => ({
  Details: (props: StepTypeDetailsProps<ReturnState>) =>
    React.createElement(Details, props),
  customTitle(state: ReturnState) {
    return state.customTitle;
  },
  title: "Return",
  valid(state: ReturnState) {
    return isReturnStepValid(state).valid;
  },
  Icon: ReturnIcon,
  iconColor: "success",
  additionalIcon(
    state: ReturnState,
    team: Team | undefined,
    flowType: FlowType,
  ) {
    const shouldRenderExchangeAgentIcon =
      flowType === FlowType.RETURN &&
      team &&
      !state.exchangeAgentOverrides?.disableExchangeAgent &&
      isExchangeAgentEnabledForTeam(
        team.settings.returnsAiExchange,
        team.settings.exchangeAgent,
        team,
      ) &&
      state.options.some(
        (option) => option.type === ReturnOptionMethod.EXCHANGE,
      );
    if (!shouldRenderExchangeAgentIcon) {
      return null;
    }
    return (
      <ZapIconFilled
        style={{
          color: "var(--redo-component-colors-utility-blue-utility-blue-500)",
        }}
      />
    );
  },
  description(state: ReturnState) {
    return state.options
      .filter(Boolean)
      .map(
        (option) =>
          ({
            [ReturnOptionMethod.STORE_CREDIT]: "Store credit",
            [ReturnOptionMethod.EXCHANGE]: "Exchange",
            [ReturnOptionMethod.REFUND]: "Refund",
            [ReturnOptionMethod.REPAIR]: "Repair",
          })[option.type!],
      )
      .join(", ");
  },
  downstream() {
    return [];
  },
  maxDownstream() {
    return 0;
  },
  empty: {
    options: [],
    labelUpcharge: true,
    labelUpchargeType: ModelStep.LabelUpchargeType.FIXED,
    labelUpchargeValue: ModelStep.DEFAULT_LABEL_UPCHARGE_VALUE,
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state: ReturnState) {
    return state;
  },
  toModel(state: ReturnState): ModelStep.Return {
    const exchangeAgentOverrides = buildExchangeAgentOverrides(
      state.exchangeAgentOverrides?.disableExchangeAgent,
      state.exchangeAgentOverrides?.disableVariantRecommendations,
      state.exchangeAgentOverrides?.disableProductRecommendations,
    );
    return {
      customTitle: state.customTitle,
      type: ModelStep.RETURN,
      options: state.options.map((option) => {
        const method = getModelReturnOptionMethod(
          option.type!,
          option.creditType!,
        );
        const flat = +option.adjustmentFlat!;
        return {
          adjustment: {
            flat,
            proportion: +option.adjustmentPercent! / 100 + 1,
            currencyOverrides: option.currencyOverrides
              ? Object.fromEntries(
                  Object.entries(option.currencyOverrides)
                    .filter(([, v]) => v !== undefined && !isNaN(+v))
                    .map(([k, v]) => [k, signCurrencyOverride(+v, flat)]),
                )
              : undefined,
            perCurrencyAmountsEnabled: option.perCurrencyAmountsEnabled,
          },
          labelPayer: option.labelPayer,
          method,
          ship: option.ship,
          manual_label: option.manual_label,
          exchangeTypes: option.exchangeTypes,
          chargeCustomerForNewOrderShipping:
            option.chargeCustomerForNewOrderShipping,
          variantExchangeAddOn: option.variantExchangeAddOn,
          variantExchangeAddOnVariantId: option.variantExchangeAddOnVariantId,
          variantExchangeAddOnTitle: option.variantExchangeAddOnTitle,
          variantExchangeAddOnAcceptButton:
            option.variantExchangeAddOnAcceptButton,
          variantExchangeAddOnRejectButton:
            option.variantExchangeAddOnRejectButton,
          isUseItOrLoseIt: option.isUseItOrLoseIt,
          refundPointsRedeemedForItem: option.refundPointsRedeemedForItem,
          deductEarnedPointsFromRefund: option.deductEarnedPointsFromRefund,
          reimburseForFinalSale: option.reimburseForFinalSale,
        };
      }),
      flatShipping: state.flatShipping,
      flatShippingRate: state.flatShippingRate,
      flatShippingCurrencyOverrides: state.flatShippingCurrencyOverrides,
      flatShippingPerCurrencyAmountsEnabled:
        state.flatShippingPerCurrencyAmountsEnabled,
      dropoffFees: state.dropoffFees,
      coverPackagePickup: state.coverPackagePickup,
      labelUpcharge: state.labelUpcharge,
      labelUpchargeType: state.labelUpchargeType,
      labelUpchargeValue: state.labelUpchargeValue,
      ...(exchangeAgentOverrides ? { exchangeAgentOverrides } : {}),
    };
  },
  fromModel(model: ModelStep.Return): ReturnState {
    return returnFromModel(model);
  },
});

export { type ReturnState } from "@redotech/redo-model/return-flow-step-valid";
